import MagicWandIcon from "@redotech/redo-icons/magic-wand-02_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { memo, useCallback, useEffect, useRef, useState } from "react";

const UNIQUENESS_DEBOUNCE_MS = 200;

/**
 * State machine for the referral code input. Drives the input's `state`
 * prop and the helper/error text shown below.
 */
type Validity =
  | { kind: "empty" }
  | { kind: "format-invalid"; reason: string }
  | { kind: "reserved" }
  | { kind: "profanity" }
  | { kind: "checking" }
  | { kind: "taken" }
  | { kind: "available" };

interface ReferralCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Called whenever the input's validity changes. The parent uses this to
   * decide whether the submit button is enabled. We treat `empty` as
   * valid (the field is optional — server will auto-generate).
   */
  onValidityChange?: (valid: boolean) => void;
  /**
   * Server-side uniqueness check. Caller wires this to the admin SDK's
   * `checkReferralCodeAvailable` so the input stays test-isolated.
   */
  checkAvailable: (
    code: string,
    opts: { signal: AbortSignal },
  ) => Promise<{ available: boolean }>;
  /**
   * When set, the trailing magic-wand button populates the field with
   * `generateReferralCodeFromName(nameForAutoGen)`. Disabled when the
   * name is empty.
   */
  nameForAutoGen?: string;
  disabled?: boolean;
}

export const ReferralCodeInput = memo(function ReferralCodeInput({
  value,
  onChange,
  onValidityChange,
  checkAvailable,
  nameForAutoGen,
  disabled,
}: ReferralCodeInputProps) {
  const [validity, setValidity] = useState<Validity>({ kind: "empty" });
  const abortRef = useRef<AbortController | null>(null);

  // Notify parent whenever validity changes. Treat empty as valid since
  // the field is optional (server auto-generates).
  useEffect(() => {
    if (!onValidityChange) {
      return;
    }
    const valid = validity.kind === "empty" || validity.kind === "available";
    onValidityChange(valid);
  }, [validity.kind, onValidityChange]);

  // Validation pipeline. Sync checks fire immediately; profanity + DB
  // uniqueness fire on a 200ms debounce. Aborts in-flight requests when
  // the value changes again before they resolve.
  useEffect(() => {
    if (value.length === 0) {
      setValidity({ kind: "empty" });
      abortRef.current?.abort();
      return;
    }

    let cancelled = false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    void (async () => {
      const { validateReferralCodeFormat, containsProfanity } = await import(
        "@redotech/redo-partners-core/partner-account/referral-code"
      );
      if (cancelled) {
        return;
      }

      const upper = value.trim().toUpperCase();
      const format = validateReferralCodeFormat(upper);
      if (!format.valid) {
        setValidity(
          format.reason === "format"
            ? {
                kind: "format-invalid",
                reason: "Use 3–20 characters, A–Z and 0–9 only.",
              }
            : { kind: "reserved" },
        );
        return;
      }

      setValidity({ kind: "checking" });
      // Debounce the network + profanity work.
      await new Promise((r) => setTimeout(r, UNIQUENESS_DEBOUNCE_MS));
      if (cancelled || controller.signal.aborted) {
        return;
      }

      if (await containsProfanity(upper)) {
        if (cancelled) {
          return;
        }
        setValidity({ kind: "profanity" });
        return;
      }

      try {
        const result = await checkAvailable(upper, {
          signal: controller.signal,
        });
        if (cancelled || controller.signal.aborted) {
          return;
        }
        setValidity({ kind: result.available ? "available" : "taken" });
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        if (cancelled) {
          return;
        }
        // Network blip — surface as available rather than blocking
        // submit. The server-side uniqueness check on submit is the
        // authoritative gate.

        console.warn("Referral code uniqueness check failed", err);
        setValidity({ kind: "available" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value, checkAvailable]);

  const handleAutoGen = useCallback(() => {
    void (async () => {
      const { generateReferralCodeFromName } = await import(
        "@redotech/redo-partners-core/partner-account/referral-code"
      );
      const candidate = generateReferralCodeFromName(nameForAutoGen ?? "");
      onChange(candidate);
    })();
  }, [nameForAutoGen, onChange]);

  const inputState = validityToInputState(validity);
  const helper = validityToHelperText(validity);
  const autoGenAvailable =
    nameForAutoGen != null && nameForAutoGen.trim().length > 0;

  return (
    <RedoTextInput
      description={helper.description}
      errorText={helper.errorText}
      label="Referral code"
      maxLength={20}
      placeholder="Auto-generate from name"
      setValue={(v) => onChange(v.toUpperCase())}
      state={disabled ? "disabled" : inputState}
      trailingActions={
        nameForAutoGen !== undefined ? (
          <RedoButton
            // Stable accessible name so spec / a11y tools can find the
            // button regardless of state. `title` provides the
            // contextual hint about why the button is disabled.
            aria-label="Generate from name"
            disabled={disabled || !autoGenAvailable}
            hierarchy="link"
            IconLeading={MagicWandIcon}
            onClick={handleAutoGen}
            size="xs"
            title={
              autoGenAvailable
                ? "Generate from name"
                : "Enter a name to generate"
            }
          />
        ) : undefined
      }
      value={value}
    />
  );
});

function validityToInputState(
  validity: Validity,
): "default" | "error" | "readonly" {
  switch (validity.kind) {
    case "format-invalid":
    case "reserved":
    case "profanity":
    case "taken":
      return "error";
    default:
      return "default";
  }
}

function validityToHelperText(validity: Validity): {
  description?: string;
  errorText?: string;
} {
  switch (validity.kind) {
    case "empty":
      return {};
    case "format-invalid":
      return { errorText: validity.reason };
    case "reserved":
      return { errorText: "This code is reserved." };
    case "profanity":
      return { errorText: "This code is not allowed." };
    case "checking":
      return { description: "Checking availability…" };
    case "taken":
      return { errorText: "This code is already taken." };
    case "available":
      return { description: "✓ Available" };
  }
}
